/**
 * Incidents Domain Hook
 * Manages incident reports with investigation stages
 */

import { useStorageState } from '../useStorageState';
import { STORAGE_KEYS } from '../../constants/STORAGE_KEYS';
import { usePermissions } from '../usePermissions';
import { filterTombstoned } from '../../sync/tombstone';  // FT-458: hide soft-deleted incidents from reads/stats

export const useIncidentsData = () => {
  const { can, currentUser } = usePermissions();
  const { value: data = [], setValue } = useStorageState(STORAGE_KEYS.INCIDENTS, []);
  // FT-458: display/count view with tombstoned (soft-deleted) incidents removed.
  // Getters + stats read this; writes use setValue(prev=>...) on the raw array so
  // the tombstone is preserved. `data` is still exposed RAW for the mounted
  // module (which filters its own display list) + admin/restore.
  const visibleData = filterTombstoned(data);
  
  // Generate stable ID if missing
  const ensureId = (r) => {
    return r.id ?? `incident_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  };
  
  // Normalize record shape
  // INCIDENTS MODULE REWORK (2026-02-25) - Added new fields for compliance tracking
  const normalize = (record) => ({
    id: ensureId(record),
    incidentNumber: record.incidentNumber ?? '',
    date: record.date ?? new Date().toISOString(),
    time: record.time ?? '',
    type: record.type ?? 'safety', // safety, environmental, security, operational
    severity: record.severity ?? 'low', // low, medium, high, critical
    status: record.status ?? 'open', // open, investigating, actions_pending, closed
    location: record.location ?? '',
    description: record.description ?? '',
    immediateActions: record.immediateActions ?? '',
    reportedBy: record.reportedBy ?? '',
    involvedPersons: record.involvedPersons ?? [],
    witnesses: record.witnesses ?? [],
    injuries: record.injuries ?? false,
    injuryDetails: record.injuryDetails ?? '',
    environmentalImpact: record.environmentalImpact ?? false,

    // Investigation fields (embedded in incident record, not separate storage)
    investigationStatus: record.investigationStatus ?? 'pending', // pending, in_progress, completed
    investigator: record.investigator ?? '',
    rootCause: record.rootCause ?? '',
    contributingFactors: record.contributingFactors ?? [],

    // Remedial/Corrective actions (embedded in incident record, not separate storage)
    remedialActions: record.remedialActions ?? [],
    preventiveMeasures: record.preventiveMeasures ?? '',
    lessonsLearned: record.lessonsLearned ?? '',

    // Metadata - SAFEGUARD: vesselId fallback to 'default' to prevent sync issues
    vesselId: record.vesselId ?? 'default',
    voyageId: record.voyageId ?? null,
    attachments: record.attachments ?? [],
    createdAt: record.createdAt ?? new Date().toISOString(),
    updatedAt: record.updatedAt ?? new Date().toISOString(),
    closedAt: record.closedAt ?? null,
    closedBy: record.closedBy ?? null,  // AUDIT: *By for closedAt
    closedByName: record.closedByName ?? null,  // FT-06d P3: Human-readable name
    closedByRole: record.closedByRole ?? null,  // FT-06e: Role for "Name (Role)" display

    // v4.2 Audit Trail (camelCase)
    createdBy: record.createdBy ?? null,
    updatedBy: record.updatedBy ?? null,

    // v4.2 Special: Incidents conducted_by tracking
    conductedBy: record.conductedBy ?? record.investigator ?? '',
    conductedAt: record.conductedAt ?? record.date ?? new Date().toISOString(),

    // v4.2 Days 11-12: Review/Approval fields (Investigation sign-off)
    reviewedBy: record.reviewedBy ?? null,
    reviewedAt: record.reviewedAt ?? null,
    reviewStatus: record.reviewStatus ?? null, // 'pending' | 'approved' | 'rejected' | null
    reviewComments: record.reviewComments ?? null,

    // INCIDENTS MODULE REWORK (2026-02-25) - Shore escalation tracking
    escalatedToShore: record.escalatedToShore ?? false,
    escalatedAt: record.escalatedAt ?? null,
    escalatedBy: record.escalatedBy ?? null,  // AUDIT: *By for escalatedAt

    // INCIDENTS MODULE REWORK (2026-02-25) - MAIB reporting tracking (WBC3 Section 3.6)
    maibReportRequired: record.maibReportRequired ?? false,
    maibReportedAt: record.maibReportedAt ?? null,
    maibReportedBy: record.maibReportedBy ?? null,  // AUDIT: *By for maibReportedAt
    maibReference: record.maibReference ?? '',

    // INCIDENTS MODULE REWORK (2026-02-25) - Certifying Authority reporting (WBC3 Section 4.7)
    caReportRequired: record.caReportRequired ?? false,
    caReportedAt: record.caReportedAt ?? null,
    caReportedBy: record.caReportedBy ?? null,  // AUDIT: *By for caReportedAt

    // INCIDENTS MODULE REWORK (2026-02-25) - Corrective action effectiveness (ISM Code Section 9.2)
    effectivenessVerified: record.effectivenessVerified ?? false,
    effectivenessVerifiedAt: record.effectivenessVerifiedAt ?? null,
    effectivenessVerifiedBy: record.effectivenessVerifiedBy ?? null,  // AUDIT: *By for effectivenessVerifiedAt

    // INCIDENTS MODULE REWORK (2026-02-25) - Quick close tracking
    quickClosed: record.quickClosed ?? false,
    quickClosedAt: record.quickClosedAt ?? null,
    quickClosedBy: record.quickClosedBy ?? null  // AUDIT: *By for quickClosedAt
  });
  
  // Save record with permission check
  const save = (record) => {
    if (!can('create', 'incidents')) {
      throw new Error('Permission denied: Cannot create incident');
    }

    let savedRecord = null;
    const userName = currentUser?.name || 'Unknown User';
    const now = new Date().toISOString();

    setValue(prev => {
      const rec = normalize(record);
      savedRecord = rec;

      // Auto-close if all actions completed
      if (rec.status === 'actions_pending' && rec.remedialActions?.length > 0) {
        const allCompleted = rec.remedialActions.every(a => a.completed === true);
        if (allCompleted) {
          rec.status = 'closed';
          rec.closedAt = new Date().toISOString();
        }
      }

      // Check if updating existing record
      const existingIndex = prev.findIndex(x => x.id === rec.id);

      if (existingIndex === -1) {
        // Add new record with audit
        rec.createdBy = userName;
        rec.createdAt = now;
        return [rec, ...prev]; // One write → one sr:storage:set
      } else {
        // Update existing record with audit
        const next = [...prev];
        next[existingIndex] = {
          ...next[existingIndex],
          ...rec,
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
          updatedAt: now
        };
        return next; // One write → one sr:storage:set
      }
    });

    return savedRecord; // Return saved record with ID
  };
  
  // Remove record with permission check
  // FT-458: soft-delete via tombstone (stamp deleted_at + KEEP in array) so the
  // deletion propagates through the cloud∪local sync union instead of a stale
  // cloud copy resurrecting it. Mirrors shipped FT-175 crew. (No incident delete
  // UI is wired today; fixed defensively so a future wiring is safe.)
  const remove = (id) => {
    if (!can('delete', 'incidents')) {
      throw new Error('Permission denied: Cannot delete incident');
    }

    const nowIso = new Date().toISOString();
    const deletedBy = currentUser?.name || 'Unknown User';
    return setValue(prev => (Array.isArray(prev) ? prev : []).map(x =>
      x.id === id
        ? { ...x, deleted_at: nowIso, deleted_by: deletedBy, updatedAt: Date.now() }
        : x
    ));
  };

/**
 * Approve incident investigation (WBC3 Appendix 8 - SMS review requirements)
 */
const approveIncident = (id, comments = '') => {
  if (!can('update', 'incidents')) {
    throw new Error('Permission denied: Cannot approve incident');
  }

  const now = new Date().toISOString();
  const userName = currentUser?.name || 'Unknown User';
  const record = getById(id);

  if (!record) {
    throw new Error(`Incident ${id} not found`);
  }

  return save({
    ...record,
    reviewedBy: userName,
    reviewedAt: now,
    reviewStatus: 'approved',
    reviewComments: comments || '',
    investigationStatus: 'completed',
    updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
    updatedAt: now
  });
};

/**
 * Reject incident investigation (WBC3 Appendix 8 - SMS review requirements)
 */
const rejectIncident = (id, reason = '') => {
  if (!can('update', 'incidents')) {
    throw new Error('Permission denied: Cannot reject incident');
  }

  const now = new Date().toISOString();
  const userName = currentUser?.name || 'Unknown User';
  const record = getById(id);

  if (!record) {
    throw new Error(`Incident ${id} not found`);
  }

  return save({
    ...record,
    reviewedBy: userName,
    reviewedAt: now,
    reviewStatus: 'rejected',
    reviewComments: reason || '',
    investigationStatus: 'in_progress',
    updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
    updatedAt: now
  });
};

  // Get single record by ID
  const getById = (id) => {
    const record = data.find(x => x.id === id);
    return record ? normalize(record) : null;
  };
  
  // Get incidents by status (FT-458: tombstoned excluded)
  const getByStatus = (status) => {
    return visibleData
      .filter(x => x.status === status)
      .map(normalize)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Get open incidents requiring action (FT-458: tombstoned excluded)
  const getOpenIncidents = () => {
    return visibleData
      .filter(x => x.status !== 'closed')
      .map(normalize)
      .sort((a, b) => {
        // Critical severity first
        if (a.severity === 'critical' && b.severity !== 'critical') return -1;
        if (b.severity === 'critical' && a.severity !== 'critical') return 1;
        // Then by date
        return new Date(b.date) - new Date(a.date);
      });
  };

  // ============================================================
  // INCIDENTS MODULE REWORK (2026-02-25) - New Hook Methods
  // ============================================================

  /**
   * Quick close an incident with optional corrective action
   * For minor incidents that don't need full investigation
   * @param {string} id - Incident ID
   * @param {Object} options - { rootCause, resolution, correctiveAction? }
   */
  const quickCloseIncident = (id, { rootCause, resolution, correctiveAction = null }) => {
    // FT-06d P3: Only skipper/fleet/super_admin can close incidents
    const canClose = currentUser?.role === 'skipper' ||
                     currentUser?.role === 'fleet' ||
                     currentUser?.role === 'super_admin';
    if (!canClose) {
      throw new Error('Permission denied: Only Master or Fleet Manager can close incidents');
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown User';
    const record = getById(id);

    if (!record) {
      throw new Error(`Incident ${id} not found`);
    }

    const update = {
      ...record,
      rootCause: rootCause || record.rootCause,
      lessonsLearned: resolution || record.lessonsLearned,
      quickClosed: true,
      quickClosedAt: now,
      quickClosedBy: userName,
      updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
      updatedAt: now
    };

    // If corrective action provided, add it and set status to actions_pending
    if (correctiveAction) {
      const newAction = {
        id: `action_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        description: correctiveAction.description || correctiveAction,
        assignedTo: correctiveAction.assignedTo || userName,
        dueDate: correctiveAction.dueDate || null,
        status: 'open',
        completed: false,
        createdAt: now,
        createdBy: {
          userId: currentUser?.id,
          name: currentUser?.name,
          role: currentUser?.role
        }
      };
      update.remedialActions = [...(record.remedialActions || []), newAction];
      update.status = 'actions_pending';
    } else {
      // No action needed, close immediately
      // FT-06d P3: Write closure audit fields
      // FT-06e: Include closedByRole for "Name (Role)" display
      update.status = 'closed';
      update.closedAt = now;
      update.closedBy = currentUser?.id || userName;
      update.closedByName = userName;
      update.closedByRole = currentUser?.role || '';
      update.investigationStatus = 'completed';
    }

    return save(update);
  };

  /**
   * FT-06d P3: Close incident with role-gate
   * Only skipper/fleet/super_admin can close incidents
   * Writes closedBy, closedByName, closedAt for audit compliance
   * @param {string} id - Incident ID
   */
  const closeIncident = (id) => {
    // FT-06d P3: Only skipper/fleet/super_admin can close incidents
    const canClose = currentUser?.role === 'skipper' ||
                     currentUser?.role === 'fleet' ||
                     currentUser?.role === 'super_admin';
    if (!canClose) {
      throw new Error('Permission denied: Only Master or Fleet Manager can close incidents');
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown User';
    const record = getById(id);

    if (!record) {
      throw new Error(`Incident ${id} not found`);
    }

    return save({
      ...record,
      status: 'closed',
      closedAt: now,
      closedBy: currentUser?.id || userName,
      closedByName: userName,
      closedByRole: currentUser?.role || '',  // FT-06e: Role for "Name (Role)" display
      investigationStatus: 'completed',
      updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      updatedAt: now
    });
  };

  /**
   * Escalate incident to shore management
   * @param {string} id - Incident ID
   * @param {string} notes - Escalation notes
   */
  const escalateIncident = (id, notes = '') => {
    if (!can('update', 'incidents')) {
      throw new Error('Permission denied: Cannot escalate incident');
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown User';
    const record = getById(id);

    if (!record) {
      throw new Error(`Incident ${id} not found`);
    }

    return save({
      ...record,
      escalatedToShore: true,
      escalatedAt: now,
      escalatedBy: userName,
      status: record.status === 'open' ? 'investigating' : record.status,
      updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
      updatedAt: now,
      reviewComments: notes ? `${record.reviewComments || ''}\n[ESCALATED ${now}]: ${notes}`.trim() : record.reviewComments
    });
  };

  /**
   * Mark incident as reported to MAIB (Marine Accident Investigation Branch)
   * @param {string} id - Incident ID
   * @param {string} reference - MAIB reference number
   */
  const markMAIBReported = (id, reference = '') => {
    if (!can('update', 'incidents')) {
      throw new Error('Permission denied: Cannot update MAIB status');
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown User';
    const record = getById(id);

    if (!record) {
      throw new Error(`Incident ${id} not found`);
    }

    return save({
      ...record,
      maibReportRequired: true,
      maibReportedAt: now,
      maibReportedBy: userName,
      maibReference: reference,
      updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
      updatedAt: now
    });
  };

  /**
   * Mark incident as reported to Certifying Authority
   * @param {string} id - Incident ID
   */
  const markCAReported = (id) => {
    if (!can('update', 'incidents')) {
      throw new Error('Permission denied: Cannot update CA status');
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown User';
    const record = getById(id);

    if (!record) {
      throw new Error(`Incident ${id} not found`);
    }

    return save({
      ...record,
      caReportRequired: true,
      caReportedAt: now,
      caReportedBy: userName,
      updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
      updatedAt: now
    });
  };

  /**
   * Verify effectiveness of corrective actions
   * Auto-closes incident if all actions are complete
   * @param {string} id - Incident ID
   * @param {string} notes - Verification notes
   */
  const verifyEffectiveness = (id, notes = '') => {
    if (!can('update', 'incidents')) {
      throw new Error('Permission denied: Cannot verify effectiveness');
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown User';
    const record = getById(id);

    if (!record) {
      throw new Error(`Incident ${id} not found`);
    }

    const update = {
      ...record,
      effectivenessVerified: true,
      effectivenessVerifiedAt: now,
      effectivenessVerifiedBy: userName,
      updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
      updatedAt: now
    };

    if (notes) {
      update.lessonsLearned = `${record.lessonsLearned || ''}\n[EFFECTIVENESS VERIFIED ${now}]: ${notes}`.trim();
    }

    // Auto-close if all corrective actions are complete
    const allActionsComplete = (record.remedialActions || []).every(
      a => a.completed === true || a.status === 'completed'
    );
    if (allActionsComplete) {
      update.status = 'closed';
      update.closedAt = now;
      update.closedBy = userName;
      update.investigationStatus = 'completed';
    }

    return save(update);
  };

  /**
   * Complete a corrective action within an incident
   * @param {string} incidentId - Incident ID
   * @param {string} actionId - Action ID within the incident
   * @param {string} notes - Completion notes
   */
  const completeAction = (incidentId, actionId, notes = '') => {
    if (!can('update', 'incidents')) {
      throw new Error('Permission denied: Cannot complete action');
    }

    const now = new Date().toISOString();
    const userName = currentUser?.name || 'Unknown User';
    const record = getById(incidentId);

    if (!record) {
      throw new Error(`Incident ${incidentId} not found`);
    }

    const updatedActions = (record.remedialActions || []).map(action => {
      if (action.id === actionId) {
        return {
          ...action,
          completed: true,
          status: 'completed',
          completedAt: now,
          completedBy: userName,
          completionNotes: notes
        };
      }
      return action;
    });

    const update = {
      ...record,
      remedialActions: updatedActions,
      updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role }, // FT-06c
      updatedAt: now
    };

    // Check if ALL actions are now complete
    const allComplete = updatedActions.every(a => a.completed === true || a.status === 'completed');
    if (allComplete && record.effectivenessVerified) {
      // Auto-close if effectiveness already verified
      update.status = 'closed';
      update.closedAt = now;
      update.closedBy = userName;
    }

    return save(update);
  };

  // ============================================================
  // End of INCIDENTS MODULE REWORK methods
  // ============================================================

  // Get statistics (FT-458: tombstoned incidents excluded from every count)
  const getStats = () => ({
    total: visibleData.length,
    open: visibleData.filter(x => x.status === 'open').length,
    investigating: visibleData.filter(x => x.status === 'investigating').length,
    actionsPending: visibleData.filter(x => x.status === 'actions_pending').length,
    closed: visibleData.filter(x => x.status === 'closed').length,
    bySeverity: visibleData.reduce((map, record) => {
      const severity = record.severity || 'low';
      map[severity] = (map[severity] || 0) + 1;
      return map;
    }, {}),
    byType: visibleData.reduce((map, record) => {
      const type = record.type || 'safety';
      map[type] = (map[type] || 0) + 1;
      return map;
    }, {}),
    withInjuries: visibleData.filter(x => x.injuries === true).length,
    withEnvironmentalImpact: visibleData.filter(x => x.environmentalImpact === true).length,
    last30Days: visibleData.filter(x => {
      const incidentDate = new Date(x.date);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return incidentDate >= thirtyDaysAgo;
    }).length
  });
  
  return {
    // FT-458: `data` is the DISPLAY view (tombstoned excluded), normalized. The
    // mounted IncidentsModule reads this for its list/counts. Writes go through
    // save/close/etc which use setValue(prev=>...) on raw storage, so tombstones
    // are preserved. `dataWithDeleted` exposes the raw array for admin/restore.
    data: visibleData.map(normalize), // UI gets normalized shapes, tombstones removed
    dataWithDeleted: data.map(normalize), // FT-458: raw incl. tombstones
    save,
    approveIncident,  // v4.2 Days 11-12
    rejectIncident,   // v4.2 Days 11-12
    remove,
    getById,
    getByStatus,
    getOpenIncidents,
    getStats,
    normalize,
    canEdit: () => can('update', 'incidents'),
    canDelete: () => can('delete', 'incidents'),
    // INCIDENTS MODULE REWORK (2026-02-25) - New methods
    closeIncident,        // FT-06d P3: Role-gated incident closure
    quickCloseIncident,   // Quick close with optional action
    escalateIncident,     // Escalate to shore management
    markMAIBReported,     // Mark as reported to MAIB
    markCAReported,       // Mark as reported to Certifying Authority
    verifyEffectiveness,  // Verify corrective action effectiveness
    completeAction        // Complete a corrective action
  };
};